import { captureException } from '@sentry/nextjs';
import { requestServer } from '@repo/ui/src/utils/requestServer';
import {
  AssetFragment,
  BreadCrumbFragment,
  LearnFragment,
  LinkFragment,
  MeasureFragment,
  MetaFragment,
  ProductFragment,
  RecipeCategoryBodyFragment,
  RecipeFragment,
  SeoFragment,
  ShopableListFragment,
  ShopFragment,
  VideoFragment,
  PageHeaderFragment,
} from '@graphql/fragments';

import { GraphQLError } from '@repo/ui/src/utils/errors';
import { serverRuntimeConfig } from '@utils/serverRuntimeConfig';
import { cache } from 'react';

// Known keys that will throw errors. See sentry logs
const invalidList = ['/undefined', ...(serverRuntimeConfig?.invalidDeliveryKeys ?? [])];

export const getContentBySlotId = /* GraphQL */ `
  query GetContentBySlotId($slotId: String!) {
    getContentBySlotId(slotId: $slotId)
  }
`;

export const getContentByDeliveryKey = /* GraphQL */ `
  query GetContentByDeliveryKey($deliveryKey: String!) {
    getContentByDeliveryKey(deliveryKey: $deliveryKey)
  }
`;

export const PageBodyFragment = /* GraphQL */ `
  fragment PageBody on Page {
    __typename
    ...Learn
    ...Product
    ...Recipe
    ...RecipeCategoryBody
    breadcrumb {
      ...BreadCrumb
    }
    showBreadcrumb
    json
  }
`;

export const getPageBodyByDeliveryKeyOrId = /* GraphQL */ `
  query GetPage($deliveryKey: String, $deliveryId: String, $currency: String) {
    page(deliveryKey: $deliveryKey, deliveryId: $deliveryId, currency: $currency) {
      ...PageBody
    }
  }
  ${PageBodyFragment}
  ${MetaFragment}
  ${SeoFragment}
  ${LinkFragment}
  ${AssetFragment}
  ${VideoFragment}
  ${MeasureFragment}
  ${ShopableListFragment}
  ${BreadCrumbFragment}
  ${ShopFragment}
  ${ProductFragment}
  ${RecipeFragment}
  ${RecipeCategoryBodyFragment}
  ${LearnFragment}
`;
export const getFullPageByDeliveryKeyOrId = /* GraphQL */ `
  query GetPage($deliveryKey: String, $deliveryId: String, $currency: String) {
    page(deliveryKey: $deliveryKey, deliveryId: $deliveryId, currency: $currency) {
      ...PageBody
      header {
        ...PageHeader
      }
      footer {
        json
      }
    }
  }
  ${PageHeaderFragment}
  ${PageBodyFragment}
  ${MetaFragment}
  ${SeoFragment}
  ${LinkFragment}
  ${AssetFragment}
  ${VideoFragment}
  ${MeasureFragment}
  ${ShopableListFragment}
  ${BreadCrumbFragment}
  ${ShopFragment}
  ${ProductFragment}
  ${RecipeFragment}
  ${RecipeCategoryBodyFragment}
  ${LearnFragment}
`;

const getPageContentBySlotId = cache(async (slotId: string, deliveryKey: string, preview?: boolean) => {
  try {
    const data = await requestServer(
      {
        document: getContentBySlotId,
        variables: {
          slotId,
        },
        requestHeaders: {
          'Accept-Language': 'en-US',
        },
      },
      { tags: [`page-${deliveryKey}`], revalidate: preview ? 0 : serverRuntimeConfig.revalidate.pageTime },
    );

    const content = { body: {} };

    if (data?.getContentBySlotId !== null) {
      content.body = JSON.parse(data.getContentBySlotId);
    } else {
      // eslint-disable-next-line
      console.info('Response Data is null');
    }

    return content;
  } catch (e) {
    captureException(e);
    return [];
  }
});

const isValidKey = (key: string) => {
  // Should not include ":", ";", ",", ".", "{", "}", or " " (space)
  const invalidKeySyntaxRegex = /[:;,.{}\s]/gm;
  // key should be truthy and not include any invalid characters or be part of known invalid list
  return !!key && !invalidKeySyntaxRegex.test(key) && !invalidList.includes(key);
};

export const getPageContentByDeliveryKey = cache(
  async (deliveryKey: string, preview?: boolean): Promise<any> => {
    if (!isValidKey(deliveryKey)) {
      throw new GraphQLError('Content does not exist', 404);
    }
    const previewHeader = preview
      ? {
          'x-content-preview': 'true',
        }
      : null;

    const data = await requestServer({
      document: getContentByDeliveryKey,
      variables: {
        deliveryKey,
      },
      requestHeaders: {
        'Accept-Language': 'en-US',
        ...previewHeader,
      },
    });

    const content = { body: {} };

    if (data?.getContentByDeliveryKey !== null) {
      content.body = JSON.parse(data.getContentByDeliveryKey);
    } else {
      console.info(`Response Data is null for ${deliveryKey}`);
    }

    return content;
  },
);

interface GetPageByDeliveryIdProps {
  deliveryId: string;
  deliveryKey?: never;
  currency?: never;
  locale?: never;
  preview?: boolean;
  includeLayout?: boolean;
}
interface GetPageByDeliveryKeyProps {
  deliveryId?: never;
  deliveryKey: string;
  currency: string;
  locale: string;
  preview?: boolean;
  includeLayout?: boolean;
}

export type GetPageContentProps = GetPageByDeliveryIdProps | GetPageByDeliveryKeyProps;

export const getPageContent = cache(
  async ({
    deliveryKey,
    deliveryId,
    currency,
    locale,
    preview,
    includeLayout = preview,
  }: GetPageContentProps): Promise<any> => {
    if (!deliveryId && !isValidKey(deliveryKey!)) {
      throw new GraphQLError('Content does not exist', 404);
    }
    const headers = new Headers();
    if (preview) {
      headers.set('x-content-preview', 'true');
    }
    if (locale) {
      headers.set('Accept-Language', locale);
    }
    const requestHeaders = Object.fromEntries([...headers.entries()]);
    const { page } = await requestServer(
      {
        document: includeLayout ? getFullPageByDeliveryKeyOrId : getPageBodyByDeliveryKeyOrId,
        variables: {
          deliveryId,
          deliveryKey,
          currency,
        },
        requestHeaders,
      },
      {
        tags: deliveryKey ? [`page-${deliveryKey}`] : undefined,
        revalidate: preview ? 0 : serverRuntimeConfig.revalidate.pageTime,
      },
    );

    if (!page) throw new GraphQLError('Content does not exist', 404);

    return {
      ...page,
      json: page.json && JSON.parse(page.json),
    };
  },
);

export default getPageContentBySlotId;
